// src/components/organisms/document-editor/extensions/like-dislike/ui/floating-trigger.tsx
import { useTranslation } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Popover } from 'module/ui/components/atoms/popover';
import { HelperText01 } from 'module/ui/components/styles';
import {
  faEllipsis,
  faThumbsDown,
  faThumbsUp,
} from '@fortawesome/pro-regular-svg-icons';
import { faLoader } from '@fortawesome/pro-solid-svg-icons';
import { Button, Textarea } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import {
  useCreateFeedbackMutation,
  useDeleteFeedbackMutation,
  useUpdateFeedbackMutation,
} from 'queries/feedback';
import { useGetDocumentFeedbackQuery } from 'queries/feedback';
import React, { useState, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
// Types
export type FeedbackFloatingTriggerProps = {
  visible: boolean;
  feedbackNodeData: {
    sourceType: string;
    sourceId: string;
    parentType: string;
    parentId: string;
    feedbackData: {
      feedbackId: number;
      feedbackType: string;
      feedbackComment: string;
      sourceType: string;
      sourceId: string;
    };
  };
};

export type FeedbackFloatingTriggerRef = {
  update: (props: FeedbackFloatingTriggerProps) => void;
};

// Styled Components
const Hint = styled.div`
  ${HelperText01};
  color: var(--text-secondary);
`;

const Wrapper = styled.div`
  min-width: 475px;
  min-height: 232px;
  ${Hint} {
    margin-top: var(--spacing-02);
  }
`;

const HeaderWrapper = styled.div`
  padding: var(--spacing-05) var(--spacing-06);
  justify-content: center;
  border-bottom: 1px solid var(--divider-default);
  align-items: center;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
`;

const FooterWrapper = styled(CenterAlignedBox)`
  justify-content: flex-end;
  padding: var(--spacing-05);
  button {
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  padding: var(--spacing-06);
  border-bottom: 1px solid var(--divider-default);
`;

const StyledLoaderIcon = styled.div`
  width: var(--height-sm);
  height: var(--height-sm);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FloatingTrigger = styled(CenterAlignedBox).attrs({ gap: 0 })`
  display: flex;
  align-items: center;
  cursor: pointer;

  button svg {
    color: var(--icon-secondary);
  }
`;

// Constants
const POPOVER_CONTENT_CLASS_NAME = 'feedback-popover-content';

// Feedback Popover Content Component
type FeedbackPopoverContentProps = {
  onSubmit: (comment: string) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  placeholderText: string;
};

const FeedbackPopoverContent: React.FC<FeedbackPopoverContentProps> = ({
  onSubmit,
  onClose,
  isLoading,
  placeholderText,
}) => {
  const [feedbackComment, setFeedbackComment] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async () => {
    if (isEmpty(feedbackComment)) {
      return;
    }
    await onSubmit(feedbackComment);
    setFeedbackComment('');
  };

  return (
    <Wrapper>
      <HeaderWrapper>
        <FormattedMessage
          id="settings.feedback_popup.negative.title"
          defaultMessage="Feedback"
        />
      </HeaderWrapper>
      <ContentWrapper>
        <Textarea
          placeholder={placeholderText}
          disabled={isLoading}
          onChange={(e) => setFeedbackComment(e.target.value)}
          ref={inputRef}
          value={feedbackComment}
        />
      </ContentWrapper>
      <FooterWrapper>
        <Button variant="secondary" onClick={onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isEmpty(feedbackComment)}
          loading={isLoading}
        >
          <FormattedMessage id="submit" />
        </Button>
      </FooterWrapper>
    </Wrapper>
  );
};

// Main Component
const FeedbackFloatingTriggerComponent = React.forwardRef<
  FeedbackFloatingTriggerRef,
  FeedbackFloatingTriggerProps
>(({ visible, feedbackNodeData }) => {
  const { mutate: feedbackCreateMutation } = useCreateFeedbackMutation();
  const { mutate: feedbackUpdateMutation } = useUpdateFeedbackMutation();
  const { mutate: feedbackDeleteMutation } = useDeleteFeedbackMutation();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = React.useState(false);
  const { data: feedbackDataQuery } = useGetDocumentFeedbackQuery(
    feedbackNodeData.parentType,
    feedbackNodeData.parentId,
  );

  // Memoize feedback data to prevent unnecessary re-renders
  const feedbackData = React.useMemo(() => {
    return feedbackDataQuery?.find(
      (feedback) =>
        feedback.sourceId === parseInt(feedbackNodeData.sourceId) &&
        feedback.sourceType === feedbackNodeData.sourceType,
    );
  }, [
    feedbackDataQuery,
    feedbackNodeData.sourceId,
    feedbackNodeData.sourceType,
  ]);

  const [isPositiveFeeedbackStatusLoading, setPositiveFeeedbackStatusLoading] =
    React.useState(false);
  const [isNegativeFeeedbackStatusLoading, setNegativeFeeedbackStatusLoading] =
    React.useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const queryClient = useQueryClient();
  const placeholderText = useTranslation(
    'settings.feedback_popup.negative.placeholder',
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [
        'documentFeedback',
        feedbackNodeData.parentType,
        feedbackNodeData.parentId,
      ],
    });
  }, [feedbackNodeData.parentType, feedbackNodeData.parentId, queryClient]);

  const handlePositiveClick = useCallback(() => {
    if (feedbackData?.feedbackType === 'POSITIVE') {
      setPositiveFeeedbackStatusLoading(true);
      feedbackDeleteMutation(
        { feedbackId: feedbackData.feedbackId },
        {
          onSuccess: invalidate,
          onSettled: () => setPositiveFeeedbackStatusLoading(false),
        },
      );
    } else if (feedbackData?.feedbackId) {
      setPositiveFeeedbackStatusLoading(true);
      feedbackUpdateMutation(
        {
          feedbackId: feedbackData.feedbackId,
          sourceType: feedbackNodeData.sourceType,
          feedbackType: 'POSITIVE',
          sourceId: feedbackNodeData.sourceId,
        },
        {
          onSuccess: invalidate,
          onSettled: () => setPositiveFeeedbackStatusLoading(false),
        },
      );
    } else {
      setPositiveFeeedbackStatusLoading(true);
      feedbackCreateMutation(
        {
          sourceType: feedbackNodeData.sourceType,
          feedbackType: 'POSITIVE',
          sourceId: feedbackNodeData.sourceId,
        },
        {
          onSuccess: invalidate,
          onSettled: () => setPositiveFeeedbackStatusLoading(false),
        },
      );
    }
  }, [
    feedbackData,
    feedbackNodeData,
    feedbackCreateMutation,
    feedbackUpdateMutation,
    feedbackDeleteMutation,
    invalidate,
  ]);

  const handleNegativeClick = useCallback(() => {
    if (feedbackData?.feedbackType === 'NEGATIVE') {
      setNegativeFeeedbackStatusLoading(true);
      feedbackDeleteMutation(
        { feedbackId: feedbackData.feedbackId },
        {
          onSuccess: invalidate,
          onSettled: () => setNegativeFeeedbackStatusLoading(false),
        },
      );
    } else {
      setIsFeedbackModalOpen(true);
    }
  }, [feedbackData, feedbackDeleteMutation, invalidate]);

  const handleFeedbackSubmit = useCallback(
    async (comment: string) => {
      if (isEmpty(comment)) {
        return;
      }

      setNegativeFeeedbackStatusLoading(true);
      setActionLoading(true);
      try {
        if (feedbackData?.feedbackId) {
          await feedbackUpdateMutation(
            {
              feedbackId: feedbackData.feedbackId,
              sourceType: feedbackNodeData.sourceType,
              feedbackType: 'NEGATIVE',
              sourceId: feedbackNodeData.sourceId,
              feedbackComment: comment,
            },
            {
              onSuccess: invalidate,
              onSettled: () => setNegativeFeeedbackStatusLoading(false),
            },
          );
        } else {
          await feedbackCreateMutation(
            {
              sourceType: feedbackNodeData.sourceType,
              feedbackType: 'NEGATIVE',
              sourceId: feedbackNodeData.sourceId,
              feedbackComment: comment,
            },
            {
              onSuccess: invalidate,
              onSettled: () => setNegativeFeeedbackStatusLoading(false),
            },
          );
        }
      } finally {
        setActionLoading(false);
        setIsFeedbackModalOpen(false);
      }
    },
    [
      feedbackData,
      feedbackNodeData,
      feedbackCreateMutation,
      feedbackUpdateMutation,
      invalidate,
    ],
  );

  if (!visible) {
    return null;
  }

  return (
    <FloatingTrigger
      style={{ zIndex: 2000 }}
      data-rl-node
      className="feedback-trigger"
    >
      {isPositiveFeeedbackStatusLoading ? (
        <StyledLoaderIcon>
          <FontAwesomeIcon icon={faLoader} size={12} spin />
        </StyledLoaderIcon>
      ) : (
        <Tooltip title={<FormattedMessage id="feedback.positive" />}>
          <Button
            size="small"
            variant="ghost"
            onClick={handlePositiveClick}
            leftIcon={<FontAwesomeIcon icon={faThumbsUp} size={12} />}
          />
        </Tooltip>
      )}
      {isNegativeFeeedbackStatusLoading ? (
        <StyledLoaderIcon>
          <FontAwesomeIcon icon={faLoader} size={12} spin />
        </StyledLoaderIcon>
      ) : (
        <Popover
          placement="bottomLeft"
          destroyTooltipOnHide
          className={POPOVER_CONTENT_CLASS_NAME}
          open={isFeedbackModalOpen}
          content={
            isFeedbackModalOpen && (
              <FeedbackPopoverContent
                onSubmit={handleFeedbackSubmit}
                onClose={() => setIsFeedbackModalOpen(false)}
                isLoading={actionLoading}
                placeholderText={placeholderText}
              />
            )
          }
        >
          <Tooltip title={<FormattedMessage id="feedback.negative" />}>
            <Button
              size="small"
              variant="ghost"
              onClick={handleNegativeClick}
              leftIcon={<FontAwesomeIcon icon={faThumbsDown} size={12} />}
            />
          </Tooltip>
        </Popover>
      )}

      <Button
        size="small"
        variant="ghost"
        leftIcon={<FontAwesomeIcon icon={faEllipsis} size={12} />}
      />
    </FloatingTrigger>
  );
});

FeedbackFloatingTriggerComponent.displayName = 'FeedbackFloatingTrigger';

// Export memoized component with custom comparison
export const FeedbackFloatingTrigger = React.memo(
  FeedbackFloatingTriggerComponent,
  (prevProps, nextProps) => {
    // Only re-render if visible state or feedback data actually changed
    if (prevProps.visible !== nextProps.visible) {
      return false;
    }

    // Deep compare feedbackNodeData
    const prevData = prevProps.feedbackNodeData;
    const nextData = nextProps.feedbackNodeData;

    return (
      prevData.sourceId === nextData.sourceId &&
      prevData.sourceType === nextData.sourceType &&
      prevData.parentId === nextData.parentId &&
      prevData.parentType === nextData.parentType &&
      JSON.stringify(prevData.feedbackData) ===
        JSON.stringify(nextData.feedbackData)
    );
  },
);
